import axios, { type AxiosError } from "axios";
import type {
  InstantNotificationSetting,
  InstantNotificationSettings,
  NotificationSpacesByCategoryResponse,
  PeriodicNotificationSettings,
} from "@/features/user-profile/types";
import { api } from "@/lib/axios";
import type { NotificationResponse } from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

export const notificationApi = {
  getNotificationById: async (
    notificationId: number,
  ): Promise<NotificationResponse> => {
    try {
      const response = await api.get<NotificationResponse>(
        `notification/${notificationId}`,
      );

      if (!response.data) {
        throw new Error("Empty response from notification API");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("Notification API Error:", error.response.data);
        throw error;
      }
      throw new Error(
        "Unexpected error occurred while fetching notification information",
      );
    }
  },

  getInstantNotificationSettings:
    async (): Promise<InstantNotificationSettings> => {
      try {
        const response = await api.get<InstantNotificationSettings>(
          "notification-settings",
        );

        if (!response.data) {
          throw new Error("Empty response from notification settings API");
        }

        return response.data;
      } catch (error: unknown) {
        if (isAxiosError(error)) {
          if (!error.response) {
            throw new Error("Please check your connection");
          }
          console.error(
            "Notification Settings API Error:",
            error.response.data,
          );
          throw error;
        }
        throw new Error(
          "Unexpected error occurred while fetching notification settings",
        );
      }
    },

  updateInstantNotificationSettings: async (
    settings: Record<string, InstantNotificationSetting>,
  ): Promise<InstantNotificationSettings> => {
    try {
      const response = await api.put<InstantNotificationSettings>(
        "notification-settings",
        { settings },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      if (!response.data) {
        throw new Error("Empty response from notification settings update API");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error(
          "Notification Settings Update API Error:",
          error.response.data,
        );
        throw error;
      }
      throw new Error(
        "Unexpected error occurred while updating notification settings",
      );
    }
  },

  getNotificationSpacesByCategory: async (
    category: string,
  ): Promise<NotificationSpacesByCategoryResponse> => {
    try {
      const response = await api.get<NotificationSpacesByCategoryResponse>(
        `notification-settings/spaces-by-category/${category}`,
      );

      if (!response.data) {
        throw new Error(
          "Empty response from notification spaces-by-category API",
        );
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error(
          "Notification Spaces-by-Category API Error:",
          error.response.data,
        );
        throw error;
      }
      throw new Error(
        "Unexpected error occurred while fetching notification spaces-by-category",
      );
    }
  },

  updateNotificationSpaceCategory: async (
    spaceId: number,
    category: string,
    enabled: boolean,
  ): Promise<NotificationSpacesByCategoryResponse["items"][number]> => {
    try {
      const response = await api.put<
        NotificationSpacesByCategoryResponse["items"][number]
      >(
        `notification-settings/spaces/${spaceId}/categories/${category}`,
        { enabled },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      if (!response.data) {
        throw new Error(
          "Empty response from notification space category update API",
        );
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error(
          "Notification Space Category Update API Error:",
          error.response.data,
        );
        throw error;
      }
      throw new Error(
        "Unexpected error occurred while updating notification space category",
      );
    }
  },

  getPeriodicNotificationSettings:
    async (): Promise<PeriodicNotificationSettings> => {
      try {
        const response =
          await api.get<PeriodicNotificationSettings>("mail-summary");

        if (!response.data) {
          throw new Error("Empty response from mail summary settings API");
        }

        return response.data;
      } catch (error: unknown) {
        if (isAxiosError(error)) {
          if (!error.response) {
            throw new Error("Please check your connection");
          }
          console.error(
            "Mail Summary Settings API Error:",
            error.response.data,
          );
          throw error;
        }
        throw new Error(
          "Unexpected error occurred while fetching mail summary settings",
        );
      }
    },

  updatePeriodicNotificationSettings: async (settings: {
    interval: number;
    limit_spaces_mode: number;
    limit_spaces: string[];
    activities: string[];
  }): Promise<PeriodicNotificationSettings> => {
    try {
      const response = await api.put<PeriodicNotificationSettings>(
        "mail-summary",
        settings,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      if (!response.data) {
        throw new Error("Empty response from mail summary settings update API");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error(
          "Mail Summary Settings Update API Error:",
          error.response.data,
        );
        throw error;
      }
      throw new Error(
        "Unexpected error occurred while updating mail summary settings",
      );
    }
  },
};
